import { useAtomValue } from 'jotai';

import { scheduledPatientInformationAtom } from '../schedule-preview/state';
import { inSessionPatientInformationAtom } from '../state';
import { useGetPatientByShortId } from '../useGetPatientByShortId';
import { ClientInformationDrawerHeader, ClientInformationDrawerHeaderProps } from './ClientInformationDrawerHeader';
import { getAppointmentClientInfoHeader, resolveAppointmentType } from './utils';

export function ClientInformationDrawerHeaderWrapper({
  selectedPatientShortId,
  visitorName,
  ...rest
}: Omit<ClientInformationDrawerHeaderProps, 'drawerTitle'> & {
  selectedPatientShortId: string;
  visitorName?: string;
}) {
  const { data: patientChartData, loading: isPatientChartLoading } = useGetPatientByShortId(selectedPatientShortId);
  const scheduledPatientInformation = useAtomValue(scheduledPatientInformationAtom);
  const inSessionPatientInformation = useAtomValue(inSessionPatientInformationAtom);
  const lastAppointment = patientChartData?.patientChart?.lastAppointment;
  const isSelectedPatientInSession =
    !!selectedPatientShortId && inSessionPatientInformation?.patientShortId === selectedPatientShortId;
  const inSessionAppointmentShortId = isSelectedPatientInSession
    ? inSessionPatientInformation?.inSessionAppointmentShortId
    : undefined;

  // The chart's lastAppointment can lag behind admission: right after joining, it may still
  // describe the prior (already-completed) visit rather than this one. Only trust its
  // appointmentType once its own appointmentShortId confirms it's actually describing the
  // appointment the patient was admitted into.
  const isLastAppointmentTheInSessionAppointment =
    !!inSessionAppointmentShortId && lastAppointment?.appointmentShortId === inSessionAppointmentShortId;

  const appointmentType = resolveAppointmentType({
    patientShortId: selectedPatientShortId,
    candidates: [
      {
        patientShortId: isLastAppointmentTheInSessionAppointment ? selectedPatientShortId : undefined,
        appointmentType: lastAppointment?.appointmentType,
      },
      {
        patientShortId: scheduledPatientInformation?.patientShortId,
        appointmentType: scheduledPatientInformation?.appointmentType,
      },
    ],
    lastAppointment,
    isLastAppointmentLoading: isPatientChartLoading,
  });

  return (
    <ClientInformationDrawerHeader
      {...rest}
      drawerTitle={getAppointmentClientInfoHeader(appointmentType, visitorName)}
    />
  );
}
